import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { ColorEnum } from '../../../../enum/color.enum';
import styled, { keyframes } from 'styled-components';
import { action, observable } from 'mobx';
import { ellipsisText } from '../../../../util/style/ellipsis-text.util';
import { LETTER_WIDTH } from '../jobad-list.view';
import { IInjectedProps } from '../../../../interface/component/injected-props.interface';
import { BlockColorType } from '../../../../enum/block-color.enum';
import { LARGE_SCREEN_SIZE } from '../../../../constants';
import { isStringEmpty } from '../../../../util/string/is-string-empty.util';
import { CrossIcon } from '../../../icons';

const showAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const TAG_HEIGHT = 35;
const TAG_VERTICAL_PADDING = 5;
export const TAG_HORIZONTAL_PADDING = 15;
export const TAG_HORIZONTAL_MARGIN = 5;
export const SEARCH_ELEMENTS_HEIGHT = 64;

const TAG_SEARCH_SHOW_MULTI_TAGS_WIDTH = 1401;

interface ITagProps {
  backgroundColor: BlockColorType;
  color?: BlockColorType;
  disabled?: boolean;
}

export const Tag = styled.div`
  height: ${TAG_HEIGHT}px;
  display: flex;
  flex: 0 1 auto;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  padding: ${TAG_VERTICAL_PADDING}px ${TAG_HORIZONTAL_PADDING}px;
  margin: ${TAG_HORIZONTAL_MARGIN}px;
  border-radius: 100px;
  white-space: nowrap;
  background-color: ${(props: ITagProps) => ColorEnum.getColor(props.backgroundColor)(props as any)};
  color: ${(props: ITagProps) => props.color ? ColorEnum.getColor(props.color)(props as any) : ColorEnum.White};
  animation: ${showAnimation} 0.5s ease-out;
  animation: ${showAnimation} 0.5s ease-in;
  ${(props: ITagProps) => props.disabled ? '' : 'cursor: pointer;'}
`;

export const SmallTag = styled(Tag)`
  flex: 0 1 calc(20% - ${2 * TAG_HORIZONTAL_PADDING}px) !important;
  width: calc(20% - 2 * ${TAG_HORIZONTAL_PADDING}px);
  min-width: 80px;
  max-width: calc(20% - ${2 * TAG_HORIZONTAL_PADDING}px);
`;

export const LargeTag = styled(Tag)`
  flex: 0 1 calc(50% - ${2 * TAG_HORIZONTAL_PADDING}px) !important;
  width: calc(50% - ${2 * TAG_HORIZONTAL_PADDING}px);
  max-width: calc(50% - ${2 * TAG_HORIZONTAL_PADDING}px);
`;

const TagText = styled.div`
  white-space: nowrap;
  ${ellipsisText};
`;

const SearchElementsContainer = styled.div`
  flex: 0 0 calc(50% - 2px);
  width: calc(50% - 2px);
  overflow: hidden;
  height: ${SEARCH_ELEMENTS_HEIGHT}px;
  display: flex;
  align-items: center;
  padding: 5px 15px;
  color: ${props => ColorEnum.getColor('PrimaryTextColor')(props as any)};
  @media (max-width: ${LARGE_SCREEN_SIZE}px) {
    flex: 0 0 100%;
    width: 100%;
  }
`;

const TagElements = styled.div`
  display: flex;
  width: calc(100% - 35px);
  flex: 0 0 calc(100% - 35px);
  justify-content: flex-start;
  align-items: center;
`;

const InputElement = styled.input`
  border: none;
  outline-width: 0;
  font-size: 24px;
  min-width: ${(props: { placeholderString: string }) => props.placeholderString.length * LETTER_WIDTH + 2 * 8}px;
  width: 100%;
  background-color: transparent;
`;

const InputWrapper = styled.div`
  padding: 5px;
  width: 100%;
  font-size: 24px;
  position: relative;
`;

const InputPlaceholder = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  padding-right: 5px;
  width: 100%;
  pointer-events: none;
  color: ${ColorEnum.AshGray};
  ${ellipsisText}
`;

const Input = (props: { placeholder: string, value: string } & React.InputHTMLAttributes<HTMLInputElement>) => {
  const {placeholder, value, ...otherProps} = props;
  return <InputWrapper>
    {isStringEmpty(value) ? <InputPlaceholder>
      {placeholder}
    </InputPlaceholder> : undefined}
    <InputElement
        {...otherProps}
        capture={otherProps.capture as any}
        placeholderString={placeholder}
        value={value}
    />
  </InputWrapper>;
};

const StyledCross = styled(CrossIcon)`
  margin-left: 5px;
  min-width: 24px;
  min-height: 24px;
  fill: ${ColorEnum.White};
  fill: ${ColorEnum.White};
` as any;

export interface ISelectedTag {
  label: string;
  disabled: boolean;
}

interface ITagSearchProps {
  disableInput?: boolean;
  placeholder: string;
  leftIcon: React.ReactNode;
  tagList: ISelectedTag[];
  onRemoveTag?: (tag: string) => void;
  onRemoveAllButLastTag?: () => void;
  onRemoveAll: () => void;
  onSearch: (tag: string) => void;
  tagBackgroundColor?: BlockColorType;
  tagTextColor?: BlockColorType;
  tagFontSize?: number;
}

/**
 *  This is the elements container inside the search component
 */
@inject('state')
@observer
export class TagSearch extends React.Component<ITagSearchProps & IInjectedProps> {
  @observable searchValue = '';

  @action
  handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (this.searchValue !== '') {
        this.props.onSearch(this.searchValue);
      }
      this.searchValue = '';
    }
  }

  @action
  onSearchValueChange = (event) => {
    this.searchValue = event.target.value;
  }

  renderTags = () => {
    if (this.props.tagList.length > 0) {
      const tag = this.props.tagList[ this.props.tagList.length - 1 ];
      if (this.props.state.windowSize.width < TAG_SEARCH_SHOW_MULTI_TAGS_WIDTH) {
        return <SmallTag
          disabled={tag.disabled}
          backgroundColor={this.props.tagBackgroundColor}
        >
          {`+${this.props.tagList.length}`}
          <StyledCross
            onClick={() => {
              this.props.onRemoveAll();
            }}
          />
        </SmallTag>;
      }
      if (this.props.tagList.length > 1) {
        return <>
          <SmallTag
            disabled={tag.disabled}
            backgroundColor={this.props.tagBackgroundColor}
          >
            {`+${this.props.tagList.length - 1}`}
            <StyledCross
              onClick={() => {
                this.props.onRemoveAllButLastTag();
              }}
            />
          </SmallTag>
          <LargeTag
            disabled={tag.disabled}
            backgroundColor={this.props.tagBackgroundColor}
          >
            <TagText title={tag.label}>{tag.label}</TagText>
            <StyledCross
              onClick={() => {
                this.props.onRemoveTag(tag.label);
              }}
            />
          </LargeTag>
        </>;
      } else {
        return <LargeTag
          disabled={tag.disabled}
          backgroundColor={this.props.tagBackgroundColor}
          key={tag.label}
        >
          <TagText title={tag.label}>{tag.label}</TagText>
          {!tag.disabled && <StyledCross
            onClick={() => {
              this.props.onRemoveTag(tag.label);
            }}
          />}
        </LargeTag>;
      }
    }
  }

  render() {
    return (
      <SearchElementsContainer>
        {this.props.leftIcon}
        <TagElements>
          {this.renderTags()}
          {!this.props.disableInput && <Input
            type="text"
            placeholder={this.props.placeholder}
            value={this.searchValue}
            onKeyPress={this.handleKeyPress}
            onChange={this.onSearchValueChange}
          />}
        </TagElements>
      </SearchElementsContainer>
    );
  }
}
